import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { getRoutes } from "src/utilities/routes";
import { IAppUser } from "src/interface/IUser";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import { handleSendMessage } from "src/utilities";

interface BulkUpdateCreatorDialogProps {
  open: boolean;
  onClose: () => void;
  selectedFollowups: IFollowupSchedule[];
  onSuccess: () => void;
}

const BulkUpdateCreatorDialog: React.FC<BulkUpdateCreatorDialogProps> = ({
  open,
  onClose,
  selectedFollowups,
  onSuccess,
}) => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const Routes = getRoutes();
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>("");

  // دریافت لیست کارمندان (Editor) - فقط نام و شناسه
  const {
    data: editors,
    isLoading: isLoadingEditors,
    error: editorsError,
  } = useQuery({
    queryKey: ["EditorsDataSimple"],
    queryFn: async () => {
      const response = await fetch(`${Routes.UserByRoleRoutes}Editor/simple`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const result = await response.json();

      // بررسی فرمت پاسخ - ممکن است در data property باشد یا مستقیماً آرایه باشد
      if (result && Array.isArray(result)) {
        return result;
      } else if (result && result.data && Array.isArray(result.data)) {
        return result.data;
      } else if (result && result.items && Array.isArray(result.items)) {
        return result.items;
      }

      console.warn("Unexpected response format:", result);
      return [];
    },
    enabled: !!user?.token && open,
    staleTime: 5 * 60 * 1000, // 5 دقیقه کش
    gcTime: 10 * 60 * 1000, // 10 دقیقه نگهداری در کش
    retry: 2,
    retryDelay: 1000,
  });
  console.log("🚀 ~ BulkUpdateCreatorDialog ~ editors:", editors);
  console.log(
    "🚀 ~ BulkUpdateCreatorDialog ~ isLoadingEditors:",
    isLoadingEditors
  );

  // نمایش خطا در console برای debugging
  useEffect(() => {
    if (editorsError) {
      console.error("Error loading editors:", editorsError);
      handleSendMessage(
        notifications,
        "خطا در دریافت لیست کارمندان: " + editorsError.toString(),
        "error"
      );
    }
  }, [editorsError, notifications]);

  console.log("🚀 ~ BulkUpdateCreatorDialog ~ editors:", editors);

  // به‌روزرسانی گروهی کارمند
  const { mutateAsync: bulkUpdateCreator, isPending: isUpdating } = useMutation(
    {
      mutationFn: async (data: {
        followupScheduleIds: number[];
        newCreatorRef: string;
      }) => {
        const response = await fetch(
          `${Routes.FollowupsScheduleRoutes}bulk-update-creator`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${user?.token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              followupScheduleIds: data.followupScheduleIds,
              newCreatorRef: data.newCreatorRef,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(
          notifications,
          responseData.data.message || "کارمند با موفقیت تغییر یافت",
          "success"
        );
        onSuccess();
        onClose();
        setSelectedCreatorId("");
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در تغییر کارمند: " + error.toString(),
          "error"
        );
      },
    }
  );

  const handleSubmit = async () => {
    if (!selectedCreatorId) {
      handleSendMessage(
        notifications,
        "لطفاً کارمند جدید را انتخاب کنید",
        "warning"
      );
      return;
    }

    const followupIds = selectedFollowups
      .map((f) => f.id)
      .filter((id): id is number => id !== null && id !== undefined);

    if (followupIds.length === 0) {
      handleSendMessage(
        notifications,
        "هیچ پیگیری‌ای انتخاب نشده است",
        "warning"
      );
      return;
    }

    await bulkUpdateCreator({
      followupScheduleIds: followupIds,
      newCreatorRef: selectedCreatorId,
    });
  };

  const handleClose = () => {
    setSelectedCreatorId("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          تغییر کارمند برای پیگیری‌های انتخاب شده
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            تعداد پیگیری‌های انتخاب شده:
          </Typography>
          <Chip
            label={`${selectedFollowups.length} پیگیری`}
            color="primary"
            variant="outlined"
          />
        </Box>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="creator-select-label">کارمند جدید</InputLabel>
          <Select
            labelId="creator-select-label"
            id="creator-select"
            value={selectedCreatorId}
            label="کارمند جدید"
            onChange={(e) => setSelectedCreatorId(e.target.value)}
            disabled={isUpdating}
          >
            {isLoadingEditors ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                در حال بارگذاری...
              </MenuItem>
            ) : editors && editors.length > 0 ? (
              editors
                .filter((editor: IAppUser) => editor.isActive)
                .map((editor: IAppUser) => (
                  <MenuItem key={editor.id} value={editor.id}>
                    {editor.fName} {editor.lName}
                    {editor.coName && ` (${editor.coName})`}
                  </MenuItem>
                ))
            ) : (
              <MenuItem disabled>کارمندی یافت نشد</MenuItem>
            )}
          </Select>
        </FormControl>

        {selectedFollowups.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              پیگیری‌های انتخاب شده:
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: "auto" }}>
              {selectedFollowups.slice(0, 5).map((followup) => (
                <Typography key={followup.id} variant="body2" sx={{ mb: 0.5 }}>
                  • {followup.title}
                </Typography>
              ))}
              {selectedFollowups.length > 5 && (
                <Typography variant="body2" color="text.secondary">
                  و {selectedFollowups.length - 5} مورد دیگر...
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUpdating}>
          انصراف
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedCreatorId || isUpdating}
          startIcon={isUpdating ? <CircularProgress size={20} /> : null}
        >
          {isUpdating ? "در حال به‌روزرسانی..." : "تغییر کارمند"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateCreatorDialog;
